import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/features/auth/AuthContext'
import { fetchControllers } from '@/features/controllers/api'
import LoadingState from '@/components/common/LoadingState'
import ErrorState from '@/components/common/ErrorState'
import EmptyState from '@/components/common/EmptyState'

/** Controller directory — a simple list backed by the same
 * GET /portal/controllers/api/v2/controllers used by the Consolidation and
 * Maintenance Notes controller pickers, with Add/Edit wired to the
 * canonical controllers_blueprint CRUD routes. */
export default function ControllersPage() {
  const { hasPermission } = useAuth()
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['controllers'],
    queryFn: fetchControllers,
  })
  const [search, setSearch] = useState('')

  const filtered = (data ?? []).filter((c) => {
    if (!search) return true
    const q = search.toLowerCase()
    return (c.controller_name ?? '').toLowerCase().includes(q) || c.controller_id.toLowerCase().includes(q)
  })

  if (isLoading) return <LoadingState label="Loading controllers…" />
  if (isError) {
    return (
      <ErrorState message={error instanceof Error ? error.message : 'Failed to load.'} onRetry={() => refetch()} />
    )
  }

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-ink">Controllers</h1>
        {hasPermission('controllers.create') && (
          <Link
            to="/controllers/new"
            className="rounded-full bg-brand px-5 py-2 text-sm font-semibold text-white hover:opacity-90"
          >
            + New Controller
          </Link>
        )}
      </div>

      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search…"
        className="mb-4 w-full max-w-sm rounded-full border border-transparent bg-shell px-[18px] py-2.5 text-sm text-text-primary focus:border-brand focus:outline-none"
      />

      {filtered.length === 0 && <EmptyState message="No controllers match." />}

      {filtered.length > 0 && (
        <div className="overflow-x-auto rounded-2xl border border-nav-line bg-card">
          <table className="w-full text-[13.5px]">
            <thead>
              <tr className="border-b border-nav-line text-left text-xs text-nav-muted">
                <th className="px-4 py-3.5 font-semibold">Name</th>
                <th className="px-4 py-3.5 font-semibold">Controller ID</th>
                <th className="px-4 py-3.5 font-semibold">App Type</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.controller_id} className="border-b border-nav-line last:border-0 hover:bg-nav-hover">
                  <td className="px-4 py-3.5">
                    <Link to={`/controllers/${c.controller_id}/edit`} className="text-accent hover:underline">
                      {c.controller_name || c.controller_id}
                    </Link>
                  </td>
                  <td className="px-4 py-2 font-mono text-xs text-text-primary">{c.controller_id}</td>
                  <td className="px-4 py-3.5 text-ink">{c.app_type ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
